import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PANEL_VERSION } from '@app/core/version';
import { UsersService } from '@app/services/users/users.service';

export const SUPPORT_NAME_MAX = 120;
export const SUPPORT_DESC_MAX = 5000;
export const SUPPORT_NAME_COUNTER_FROM = 100;
export const SUPPORT_DESC_COUNTER_FROM = 4500;

type SupportState = 'editing' | 'sending' | 'failed' | 'done';

@Component({
  selector: 'app-support-contact-dialog',
  templateUrl: './support-contact-dialog.component.html',
  styleUrls: ['./support-contact-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SupportContactDialogComponent {
  readonly nameMax = SUPPORT_NAME_MAX;
  readonly descMax = SUPPORT_DESC_MAX;
  readonly supportEmail = 'support@trakrsuite.com';

  form: FormGroup;
  state: SupportState = 'editing';
  submitted = false;
  limitHit = false;
  serverErrors: { [field: string]: string } = {};
  noEmail: boolean;
  accountEmail: string;
  sentName = '';
  sentEmail = '';

  constructor(
    private _fb: FormBuilder,
    private _users: UsersService,
    private _router: Router,
    private dialogRef: MatDialogRef<SupportContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: { email?: string }
  ) {
    this.accountEmail = ((data && data.email) || '').trim();
    this.noEmail = this.accountEmail === '';
    this.form = this._fb.group({
      replyEmail: ['', this.noEmail ? [Validators.required, Validators.email] : []],
      name: ['', [Validators.required]],
      desc: ['', [Validators.required]]
    });
    this.dialogRef.disableClose = false;
  }

  get nameLen(): number { return (this.form.value.name || '').length; }
  get descLen(): number { return (this.form.value.desc || '').length; }
  get showNameCount(): boolean { return this.nameLen >= SUPPORT_NAME_COUNTER_FROM; }
  get showDescCount(): boolean { return this.descLen >= SUPPORT_DESC_COUNTER_FROM; }

  // Error keys (i18n) per field; empty when the field is fine or untouched.
  errorFor(field: 'replyEmail' | 'name' | 'desc'): string {
    const server = this.serverErrors[field];
    if (server) { return server; }
    if (!this.submitted && !this.form.get(field).touched) { return ''; }
    const v: string = (this.form.get(field).value || '').toString();
    if (field === 'replyEmail' && this.noEmail) {
      if (!v.trim()) { return 'support.errReplyEmailRequired'; }
      if (this.form.get('replyEmail').invalid) { return 'support.errReplyEmailInvalid'; }
    }
    if (field === 'name') {
      if (!v.trim()) { return 'support.errNameRequired'; }
      if (v.length > SUPPORT_NAME_MAX) { return 'support.errNameLong'; }
    }
    if (field === 'desc') {
      if (!v.trim()) { return 'support.errDescRequired'; }
      if (v.length > SUPPORT_DESC_MAX) { return 'support.errDescLong'; }
    }
    return '';
  }

  private isValid(): boolean {
    return !this.errorFor('name') && !this.errorFor('desc') && (!this.noEmail || !this.errorFor('replyEmail'));
  }

  onSubmit(): void {
    if (this.state === 'sending') { return; }
    this.submitted = true;
    if (!this.isValid()) { return; }

    const v = this.form.value;
    const body: any = {
      subject: v.name.trim(),
      description: v.desc.trim(),
      client: {
        app: 'panel',
        version: PANEL_VERSION,
        page: (this._router.url || '').substring(0, 200),
        user_agent: (navigator.userAgent || '').substring(0, 200)
      }
    };
    if (this.noEmail) { body.reply_email = v.replyEmail.trim(); }

    this.serverErrors = {};
    this.limitHit = false;
    this.state = 'sending';
    this.dialogRef.disableClose = true;
    this._users.onContactSupport(body).subscribe({
      next: () => {
        this.sentName = body.subject;
        this.sentEmail = this.noEmail ? body.reply_email : this.accountEmail;
        this.state = 'done';
        this.dialogRef.disableClose = false;
      },
      error: (err) => {
        this.dialogRef.disableClose = false;
        if (err && err.status === 429) {
          this.limitHit = true;
          this.state = 'editing';
        } else if (err && err.status === 422 && err.error && err.error.errors) {
          const e = err.error.errors;
          const map = { subject: 'name', description: 'desc', reply_email: 'replyEmail' };
          Object.keys(map).forEach((k) => {
            if (e[k] && e[k][0]) { this.serverErrors[map[k]] = e[k][0]; }
          });
          this.state = 'editing';
        } else {
          this.state = 'failed';
        }
      }
    });
  }

  onClose(): void {
    if (this.state !== 'sending') { this.dialogRef.close(); }
  }
}
